import { Head, Link } from '@inertiajs/react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import type { Breadcrumb } from '@/types/shared'

type PlayerDetail = {
  id: string
  name: string | null
  email?: string | null
  team?: { id: string; name: string }
  prefecture?: string | null
  birthday?: string | null
  boarding?: boolean | null
  height?: number | null
  dominantFoot?: string | null
  dominantFootLabel?: string | null
  fiftyMeterTime?: number | null
  fatherHeight?: number | null
  motherHeight?: number | null
  positions: string[]
  comment?: string | null
  videos: string[]
  profileStatus: string
  active?: boolean | null
  createdAt: string
  updatedAt: string
}

type AdminPlayerShowProps = {
  player: PlayerDetail
  breadcrumbs: Breadcrumb[]
}

const statusLabels: Record<string, string> = {
  approved: '承認済み',
  pending: '承認待ち',
  nothing: '未登録',
}

export default function AdminPlayerShow({ player, breadcrumbs }: AdminPlayerShowProps) {
  return (
    <AppShell>
      <Head title={`選手詳細｜${player.name ?? '未設定'}`} />
      <PageHeader
        title={player.name ?? '選手詳細'}
        description="選手プロフィールの詳細情報を確認できます。"
        breadcrumbs={breadcrumbs}
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card className="space-y-8" padding="lg">
          <Section title="基本情報">
            <InfoGrid
              items={[
                { label: '選手名', value: player.name ?? '未設定' },
                { label: 'メールアドレス', value: player.email ?? '未設定' },
                {
                  label: '所属チーム',
                  value: player.team ? (
                    <Link href={`/admin/teams/${player.team.id}`} className="text-brand-200 hover:text-brand-100">
                      {player.team.name}
                    </Link>
                  ) : (
                    '未設定'
                  ),
                },
                { label: '都道府県', value: player.prefecture ?? '未設定' },
                { label: '生年月日', value: formatDate(player.birthday) },
                { label: '寮生活', value: renderBoarding(player.boarding) },
              ]}
            />
          </Section>

          <Section title="身体情報">
            <InfoGrid
              items={[
                { label: '身長', value: formatHeight(player.height) },
                { label: '利き足', value: player.dominantFootLabel || '未設定' },
                { label: '50mタイム', value: formatTime(player.fiftyMeterTime) },
                { label: '父親の身長', value: formatHeight(player.fatherHeight) },
                { label: '母親の身長', value: formatHeight(player.motherHeight) },
                { label: 'ポジション', value: player.positions.length ? player.positions.join(', ') : '未設定' },
              ]}
            />
          </Section>

          <Section title="プロフィール">
            <InfoGrid
              items={[
                {
                  label: 'プロフィール承認ステータス',
                  value: (
                    <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-white/80">
                      {statusLabels[player.profileStatus] ?? statusLabels.nothing}
                    </span>
                  ),
                },
                { label: 'アクティブ状態', value: renderActive(player.active) },
              ]}
            />
            <div className="mt-6 space-y-3">
              <h3 className="text-xs uppercase tracking-wide text-white/50">自己PR</h3>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/80">
                {player.comment ? player.comment : '未設定'}
              </div>
            </div>
          </Section>

          <Section title="動画URL">
            {player.videos.length === 0 ? (
              <p className="text-sm text-white/60">登録された動画はありません。</p>
            ) : (
              <ul className="space-y-3 text-sm text-brand-100">
                {player.videos.map((url, index) => (
                  <li key={url} className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 p-3">
                    <span className="text-white/70">動画 {index + 1}</span>
                    <a href={url} target="_blank" rel="noreferrer" className="truncate text-right hover:text-brand-50">
                      {url}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </Card>

        <Card className="space-y-4" padding="lg">
          <h2 className="text-lg font-semibold text-white">システム情報</h2>
          <InfoGrid
            items={[
              { label: '登録日時', value: formatDateTime(player.createdAt) },
              { label: '更新日時', value: formatDateTime(player.updatedAt) },
            ]}
          />
        </Card>
      </div>
    </AppShell>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold text-white">{title}</h2>
      {children}
    </section>
  )
}

function InfoGrid({ items }: { items: Array<{ label: string; value: React.ReactNode }> }) {
  return (
    <dl className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {items.map((item) => (
        <div key={item.label} className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <dt className="text-xs uppercase tracking-wide text-white/40">{item.label}</dt>
          <dd className="mt-2 text-sm text-white/80">{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

function formatHeight(value?: number | null) {
  if (value == null) return '未設定'
  return `${Number(value)} cm`
}

function formatTime(value?: number | null) {
  if (value == null) return '未設定'
  return `${Number(value).toFixed(1)} 秒`
}

function renderBoarding(value?: boolean | null) {
  if (value == null) return '未設定'
  return value ? '希望する' : '希望しない'
}

function renderActive(value?: boolean | null) {
  if (value == null) return '未設定'
  return value ? 'アクティブ' : '非アクティブ'
}

function formatDate(value?: string | null) {
  if (!value) return '未設定'
  return new Date(value).toLocaleDateString('ja-JP', { year: 'numeric', month: 'numeric', day: 'numeric' })
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
